import { FormEvent, useEffect, useState } from "react";
import { Check, Eye, EyeSlash, UserCircle, X } from "@phosphor-icons/react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import {
  useActivateUserMutation,
  useGetAuthProvidersQuery,
  useLoginMutation,
  useSignupMutation,
  useSocialAuthMutation,
} from "@/redux/api-slices/apiSlice";
import { login as loginAction } from "@/redux/authSlice";

type AuthMode = "signin" | "signup";

type AccountAuthModalProps = {
  isOpen: boolean;
  initialMode?: AuthMode;
  reason?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const inputClass =
  "mt-1.5 h-11 w-full rounded-md border border-secondary-2 bg-white px-3 text-sm text-secondary-9 outline-none transition focus:border-accent-6 focus:ring-2 focus:ring-accent-2 dark:border-secondary-7";

const AccountAuthModal = ({
  isOpen,
  initialMode = "signin",
  reason,
  onClose,
  onSuccess,
}: AccountAuthModalProps) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [signin, setSignin] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [login, { isLoading: isSigningIn }] = useLoginMutation();
  const [signup, { isLoading: isSigningUp }] = useSignupMutation();
  const [socialAuth, { isLoading: isGoogleLoading }] = useSocialAuthMutation();
  const [activateUser] = useActivateUserMutation();
  const { data: authProviders, isFetching: isProvidersLoading } =
    useGetAuthProvidersQuery(undefined, { skip: !isOpen });
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [initialMode, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const finishAuth = (result: any) => {
    dispatch(loginAction(result));
    setError("");
    onSuccess?.();
    onClose();
  };

  const handleGoogleAuth = async (credential: string) => {
    if (!authProviders?.google?.webClientId) {
      setError("Google sign-in is not configured yet.");
      return;
    }
    try {
      finishAuth(await socialAuth({ token: credential }).unwrap());
    } catch (requestError: any) {
      setError(requestError?.data?.error || "Google sign-in failed. Please try again.");
    }
  };

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      let result = await login(signin).unwrap();
      if (result?.status === "inactive") {
        const confirmed = window.confirm("Your account is inactive. Would you like to reactivate it?");
        if (!confirmed) return;
        await activateUser({ userId: result._id, status: "active" }).unwrap();
        result = { ...result, status: "active" };
      }
      finishAuth(result);
    } catch (requestError: any) {
      setError(requestError?.data?.error || "Invalid email or password. Please try again.");
    }
  };

  const passwordChecks = [
    { label: "6 or more characters", valid: signupForm.password.length >= 6 },
    { label: "One uppercase letter", valid: /[A-Z]/.test(signupForm.password) },
    { label: "One lowercase letter", valid: /[a-z]/.test(signupForm.password) },
    { label: "One symbol", valid: /[^A-Za-z0-9]/.test(signupForm.password) },
  ];

  const handleSignUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!passwordChecks.every((check) => check.valid)) {
      setError("Please meet all password requirements.");
      return;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      setError("Passwords must match.");
      return;
    }
    try {
      const result = await signup({
        firstName: signupForm.firstName.trim(),
        lastName: signupForm.lastName.trim(),
        email: signupForm.email.trim(),
        password: signupForm.password,
      }).unwrap();
      finishAuth(result);
    } catch (requestError: any) {
      setError(requestError?.data?.error || "Could not create your account. Please try again.");
    }
  };

  const busy = isSigningIn || isSigningUp || isGoogleLoading;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-secondary-10/70 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-dialog-title"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="max-h-[94vh] w-full max-w-[560px] overflow-y-auto rounded-lg bg-white text-secondary-8 shadow-2xl dark:bg-secondary-9 dark:text-primary-1">
        <header className="flex items-start justify-between border-b border-secondary-1 px-5 py-4 dark:border-secondary-7">
          <div className="flex gap-3">
            <span className="mt-0.5 grid h-10 w-10 place-items-center rounded-full bg-accent-1 text-accent-7 dark:bg-accent-9/30 dark:text-accent-4">
              <UserCircle size={24} weight="fill" />
            </span>
            <div>
              <h2 id="account-dialog-title" className="font-nokia-bold text-xl">Your Ezra account</h2>
              <p className="mt-0.5 text-sm text-secondary-5 dark:text-primary-7">
                {reason || "Sign in or create an account without leaving your place."}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-secondary-1 dark:hover:bg-secondary-8" aria-label="Close account dialog">
            <X size={20} />
          </button>
        </header>

        <div className="mx-5 mt-4 grid grid-cols-2 rounded-md bg-secondary-1 p-1 dark:bg-secondary-8" role="tablist" aria-label="Account options">
          {(["signin", "signup"] as AuthMode[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={mode === tab}
              className={`h-10 rounded text-sm font-semibold transition ${mode === tab ? "bg-white text-accent-8 shadow-sm dark:bg-secondary-7 dark:text-accent-4" : "text-secondary-5 hover:text-secondary-8 dark:text-primary-7"}`}
              onClick={() => { setMode(tab); setError(""); }}
            >
              {tab === "signin" ? "Sign In" : "Sign Up"}
            </button>
          ))}
        </div>

        <div className="p-5 pt-4">
          <GoogleAuthButton
            onCredential={handleGoogleAuth}
            text={mode === "signin" ? "signin_with" : "signup_with"}
            disabled={busy || isProvidersLoading}
          />
          <div className="my-4 flex items-center gap-3 text-xs text-secondary-4">
            <span className="h-px flex-1 bg-secondary-2 dark:bg-secondary-7" />
            <span>or use email and password</span>
            <span className="h-px flex-1 bg-secondary-2 dark:bg-secondary-7" />
          </div>

          {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" role="alert">{error}</div>}

          {mode === "signin" ? (
            <form onSubmit={handleSignIn} className="space-y-4" noValidate>
              <label className="block text-sm font-semibold">Email
                <input autoFocus value={signin.email} onChange={(event) => setSignin({ ...signin, email: event.target.value })} type="email" required autoComplete="email" className={inputClass} placeholder="you@example.com" />
              </label>
              <label className="block text-sm font-semibold">Password
                <span className="relative block">
                  <input value={signin.password} onChange={(event) => setSignin({ ...signin, password: event.target.value })} type={showPassword ? "text" : "password"} minLength={6} required autoComplete="current-password" className={`${inputClass} pr-11`} />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-1 top-2.5 grid h-9 w-9 place-items-center text-secondary-5" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </span>
              </label>
              <div className="flex items-center justify-between gap-4">
                <Link to="/forgot-password" onClick={onClose} className="text-sm font-semibold text-accent-7 hover:underline">Forgot password?</Link>
                <button disabled={busy} className="h-11 rounded-md bg-accent-6 px-6 font-semibold text-white transition hover:bg-accent-7 disabled:cursor-not-allowed disabled:opacity-60">
                  {isSigningIn ? "Signing in..." : "Sign In"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-4" noValidate>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-semibold">First name
                  <input value={signupForm.firstName} onChange={(event) => setSignupForm({ ...signupForm, firstName: event.target.value })} required minLength={2} maxLength={30} autoComplete="given-name" className={inputClass} />
                </label>
                <label className="block text-sm font-semibold">Last name
                  <input value={signupForm.lastName} onChange={(event) => setSignupForm({ ...signupForm, lastName: event.target.value })} required minLength={2} maxLength={30} autoComplete="family-name" className={inputClass} />
                </label>
              </div>
              <label className="block text-sm font-semibold">Email
                <input value={signupForm.email} onChange={(event) => setSignupForm({ ...signupForm, email: event.target.value })} type="email" required maxLength={35} autoComplete="email" className={inputClass} />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-semibold">Password
                  <span className="relative block">
                    <input value={signupForm.password} onChange={(event) => setSignupForm({ ...signupForm, password: event.target.value })} type={showPassword ? "text" : "password"} required autoComplete="new-password" className={`${inputClass} pr-11`} />
                    <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-1 top-2.5 grid h-9 w-9 place-items-center text-secondary-5" aria-label={showPassword ? "Hide passwords" : "Show passwords"}>
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </span>
                </label>
                <label className="block text-sm font-semibold">Confirm password
                  <input value={signupForm.confirmPassword} onChange={(event) => setSignupForm({ ...signupForm, confirmPassword: event.target.value })} type={showPassword ? "text" : "password"} required autoComplete="new-password" className={inputClass} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs text-secondary-5">
                {passwordChecks.map((check) => <span key={check.label} className={`flex items-center gap-1.5 ${check.valid ? "text-emerald-700 dark:text-emerald-400" : ""}`}><Check size={13} weight="bold" />{check.label}</span>)}
              </div>
              <div className="flex justify-end">
                <button disabled={busy} className="h-11 rounded-md bg-accent-6 px-6 font-semibold text-white transition hover:bg-accent-7 disabled:cursor-not-allowed disabled:opacity-60">
                  {isSigningUp ? "Creating account..." : "Create Account"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountAuthModal;
